import { config, isDebugEnabled } from '../config/environment';

export interface SubscriptionStatus {
  isSubscribed: boolean;
  trialEnd: number | null;
  subscriptionEnd: number | null;
}

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.mainUrl;
  }

  private async request(endpoint: string, options: RequestOptions = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    if (isDebugEnabled()) {
      console.log(`API Request: ${options.method || 'GET'} ${url}`, options);
    }

    const response = await fetch(url, options);
    
    if (isDebugEnabled()) {
      console.log(`API Response: ${response.status} from ${url}`);
    }

    return response;
  }

  async generateResume(resumeData: any): Promise<Blob> {
    const response = await this.request('/api/generate/resume', {
      method: 'POST',
      headers: {
        Accept: 'application/pdf',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resumeData),
    });
    return response.blob();
  }

  async generateSource(jsonData: any): Promise<Blob> {
    const response = await this.request('/api/generate/source', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(jsonData),
      credentials: 'same-origin',
    });
    return response.blob();
  }

  async uploadJSON(formData: FormData): Promise<any> {
    const response = await this.request('/api/upload', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(await response.text());
    }
    
    return response.json();
  }

  async chat(content: string, messages: any[], fileContent?: { name: string, content: string, type: string }, currentResume?: any): Promise<any> {
    const response = await this.request('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, messages, fileContent, currentResume }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || 'Chat request failed');
      } catch (e) {
        throw new Error(errorText || 'Chat request failed');
      }
    }
    
    return response.json();
  }

  async createSubscription(userId: string): Promise<{ clientSecret: string }> {
    const response = await this.request('/api/create-subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error('Failed to create subscription');
    }

    return response.json();
  }

  async getSubscriptionStatus(userId: string): Promise<SubscriptionStatus> {
    const response = await this.request(`/api/subscription-status/${userId}`);

    if (!response.ok) {
      throw new Error('Failed to get subscription status');
    }

    return response.json();
  }

  async createCheckoutSession(userId: string, email: string): Promise<{ sessionId: string }> {
    const response = await this.request('/api/create-checkout-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    return response.json();
  }

  async createPortalSession(userId: string): Promise<{ url: string }> {
    const response = await this.request('/api/create-portal-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create portal session');
    }

    return response.json();
  }
}

export const api = new ApiService(); 